import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchEpicPlan } from '../../../../lib/api/epics';
import type { EpicDetail, EpicSession, StorySpec } from '../../../../../../shared/types/epics';
import { buildRetryClusters } from '../../../../../../shared/lib/retryClusters';
import { scoreStory } from '../../../../../../shared/lib/verification';
import { Card, CardHeader, CardBody, EmptyState } from '../../../../components/ui';
import { storyAnchorId } from '../format';
import { VerificationBadge, FlagChip } from './verification';
import { LiveAgentCard } from './LiveAgentCard';
import { StoryCard } from './StoryCard';
import { ClusterLegend } from './RetryClusters';
import { OrphanSessionsCard } from './OrphanSessionsCard';

// FORGE-228 (#2 comprehension + #3 cognitive-surrender) — an at-a-glance
// "what deserves a human look" surface. Ranks the epic's SETTLED stories by a
// review-risk score (low verification + churn + size + unreviewed) so a story
// that reached "green" without an independent review, or after a lot of churn,
// doesn't slip past you. In-flight / code-free stories are excluded.
const ReviewAttentionCard: React.FC<{
  epic: EpicDetail;
  sessionsByStory: Map<string, EpicSession[]>;
}> = ({ epic, sessionsByStory }) => {
  const rows = useMemo(() => {
    return epic.stories
      .map((story) => ({
        story,
        v: scoreStory(story, buildRetryClusters(sessionsByStory.get(story.id) ?? [])),
      }))
      .filter((r) => r.v.band !== 'pending' && r.v.band !== 'na');
  }, [epic.stories, sessionsByStory]);

  if (rows.length === 0) return null;

  const flagged = rows
    .filter((r) => r.v.flags.length > 0 || r.v.band === 'weak')
    .sort((a, b) => b.v.risk - a.v.risk);
  const avg = Math.round(rows.reduce((s, r) => s + (r.v.score ?? 0), 0) / rows.length);

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">What needs your review</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Guards against verification debt and rubber-stamping — flags stories that reached
            &ldquo;done&rdquo; without an independent review or after heavy churn.
          </div>
        </div>
        <div className="text-right text-xs text-slate-600 shrink-0">
          <div className="font-mono tabular-nums text-slate-900 font-semibold" data-testid="epic-avg-verification">
            {avg}% avg
          </div>
          <div>{flagged.length} flagged of {rows.length}</div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {flagged.length === 0 ? (
          <div className="px-4 py-3 text-sm text-slate-600" data-testid="review-all-clear">
            All {rows.length} settled {rows.length === 1 ? 'story was' : 'stories were'} independently
            reviewed and reached a verified state — nothing flagged.
          </div>
        ) : (
          <ul data-testid="review-attention-list">
            {flagged.slice(0, 8).map(({ story, v }) => (
              <li
                key={story.id}
                className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-100 first:border-t-0"
              >
                <VerificationBadge v={v} />
                <a href={`#${storyAnchorId(story.id)}`} className="min-w-0 flex-1 group">
                  <div className="font-mono text-2xs text-slate-500 truncate">{story.id}</div>
                  <div className="text-sm text-slate-900 truncate group-hover:underline">
                    {story.title || <span className="italic text-slate-400">(untitled)</span>}
                  </div>
                </a>
                <div className="flex flex-wrap gap-1 justify-end max-w-[45%]">
                  {v.flags.map((f) => (
                    <FlagChip key={f} flag={f} />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

export const StoriesAndSessions: React.FC<{
  epic: EpicDetail;
  sessionsByStory: Map<string, EpicSession[]>;
  orphanSessions: EpicSession[];
  onOpenTranscript: (storyId: string, agentType: string, taskArn?: string | null) => void;
}> = ({ epic, sessionsByStory, orphanSessions, onOpenTranscript }) => {
  const queryClient = useQueryClient();

  // FORGE-181: fetch the epic plan once (all per-story specs) and index by story
  // index. Graceful: a 404 (no plan stored) leaves the map empty and StoryCard
  // simply omits the Spec panel.
  const planQuery = useQuery({
    queryKey: ['epic-plan', String(epic.id)],
    queryFn: () => fetchEpicPlan(String(epic.id)),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const specByIdx = useMemo(() => {
    const m = new Map<number, StorySpec>();
    for (const s of planQuery.data?.stories ?? []) m.set(s.idx, s);
    return m;
  }, [planQuery.data]);

  const epicLiveAgents = epic.liveAgents ?? [];

  if (epic.stories.length === 0 && epic.sessions.length === 0 && epicLiveAgents.length === 0) {
    return (
      <EmptyState
        icon="🧭"
        title="No stories yet"
        description="The epic planner hasn't produced a plan yet. Once it does, stories and agent runs will appear here."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Epic-level agents running right now (epic-planner / product-manager /
          solution-analyst) — they don't own a story row, so this is the only
          place their in-flight run shows before it completes. */}
      {epicLiveAgents.length > 0 && (
        <div className="space-y-2">
          {epicLiveAgents.map((live, i) => (
            <LiveAgentCard key={`${live.agentType}-${i}`} live={live} />
          ))}
        </div>
      )}
      <ReviewAttentionCard epic={epic} sessionsByStory={sessionsByStory} />
      <ClusterLegend />
      {epic.stories.map((story) => (
        <StoryCard
          key={story.id}
          epicId={epic.id}
          story={story}
          spec={story.numericId !== null ? specByIdx.get(story.numericId) : undefined}
          sessions={sessionsByStory.get(story.id) ?? []}
          onOpenTranscript={(agentType, taskArn) => onOpenTranscript(story.id, agentType, taskArn)}
          onActionSuccess={() =>
            queryClient.invalidateQueries({ queryKey: ['epic', String(epic.id)] })
          }
        />
      ))}
      {orphanSessions.length > 0 && (
        <OrphanSessionsCard
          sessions={orphanSessions}
          onOpenTranscript={onOpenTranscript}
        />
      )}
    </div>
  );
};
